import { Trans } from "react-i18next";
import i18next from "src/i18n/config";

import { SubscriptionEvents } from "@dashboard/devices/types";
import { getEventColors } from "@dashboard/overview/components/EventChartCard/EventChartCard.utils";
import { Theme } from "@mui/material";

import { getActorTitle } from "./shared/eventHelpers";
import { EventContentResult, EventContext, RendererProps } from "./shared/eventTypes";
import { renderUnknownEventContent } from "./unknown";

type SubscriptionCancelledEvent = Extract<SubscriptionEvents, { eventTypeName: "SubscriptionEvent.Cancelled" }>;
const renderSubscriptionCancelled = (props: RendererProps<SubscriptionCancelledEvent>): EventContentResult | null => {
    const { event, theme } = props;

    const actorLabel = getActorTitle(event, i18next.t("common:status.unknown"));
    const color = getEventColors(theme);

    if (!event.data) {
        return {
            color: color[event.level],
            header: i18next.t("modals:eventModals.subscriptionCancelledTitle"),
            component: (
                <Trans
                    ns="modals"
                    i18nKey="eventModals.subscriptionCancelledDescriptionWithoutData"
                    values={{
                        actorTitle: actorLabel,
                        entityId: event.entityId,
                    }}
                />
            ),
        };
    }

    const reason = event.data.reason;
    const chargeId = event.data.chargeId;
    const hasChargeId = !!chargeId?.length;
    const hasReason = !!reason?.length;

    if (!hasReason) {
        return {
            color: color[event.level],
            header: i18next.t("modals:eventModals.subscriptionCancelledTitle"),
            component: (
                <Trans
                    ns="modals"
                    i18nKey={
                        hasChargeId
                            ? "eventModals.subscriptionCancelledDescriptionWithoutReason"
                            : "eventModals.subscriptionCancelledDescriptionWithoutReasonAndChargeId"
                    }
                    values={{
                        actorTitle: actorLabel,
                        chargeId,
                    }}
                />
            ),
        };
    }

    return {
        color: color[event.level],
        header: i18next.t("modals:eventModals.subscriptionCancelledTitle"),
        component: (
            <Trans
                ns="modals"
                i18nKey={
                    hasChargeId
                        ? "eventModals.subscriptionCancelledDescription"
                        : "eventModals.subscriptionCancelledDescriptionWithoutChargeId"
                }
                values={{
                    actorTitle: actorLabel,
                    chargeId,
                    reason,
                }}
            />
        ),
    };
};

export const renderSubscriptionEventContent = (
    event: SubscriptionEvents,
    context: EventContext,
    theme: Theme,
): EventContentResult | null => {
    switch (event.eventTypeName) {
        case "SubscriptionEvent.Cancelled":
            return renderSubscriptionCancelled({ event, context, theme });
        default:
            return renderUnknownEventContent({ event, context, theme });
    }
};
